import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikState } from 'formik';
import { Button, Form, Grid, Header, Segment } from 'semantic';
import CheckboxField from 'components/form-fields/formik/CheckboxField';
import DateField from 'components/form-fields/formik/DateField';
import DropdownField from 'components/form-fields/formik/DropdownField';
import Divider from 'components/Sidebar/Divider';
import { TariffFormFields } from 'screens/GroupsAndPolicies/Tariff/TariffFormFields';
import {
  AuthorizationMethod,
  AuthorizationMethodValues,
  CustomTariffRule as CustomTariffRuleType,
  DropdownOption,
} from 'screens/GroupsAndPolicies/types';
import { FormController } from 'screens/GroupsAndPolicies/FormController';
import { TariffPreview } from 'screens/GroupsAndPolicies/Tariff/TariffPreview';
import { buildFormPath } from 'screens/GroupsAndPolicies/formpath';
import { CustomerGroupModal } from 'screens/GroupsAndPolicies/CustomerGroup/CustomerGroupModal';

// TODO: needs to become API req
function getCustomerGroups(): DropdownOption[] {
  return [
    {
      key: 'CUSTOMER_GROUP_1',
      text: 'Customer group 1',
      value: 'CUSTOMER_GROUP_1',
    },
    {
      key: 'CUSTOMER_GROUP_2',
      text: 'Customer group 2',
      value: 'CUSTOMER_GROUP_2',
    },
  ];
}

const getPaymentOptions = (): DropdownOption<AuthorizationMethod>[] => {
  const { t } = useTranslation();

  return AuthorizationMethodValues.map((value) => ({
    key: value,
    value,
    text: t(
      `chargingPolicy.pricing.custom.priceSelection.${value.toLowerCase()}`,
      value
    ),
  }));
};

type Props = {
  values: FormikState<CustomTariffRuleType>['values'];
  setFieldValue: (field: string, value: any) => void;
  basePath?: string;
};

export const CustomTariffRuleFormFields: React.FC<Props> = ({
  values,
  setFieldValue,
  basePath,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Grid>
        <Grid.Row>
          <Grid.Column width={4}>
            <Header
              as="h4"
              content={t(
                'chargingPolicy.pricing.custom.title',
                'Custom Tariff'
              )}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Segment>
        <CheckboxField
          toggle
          name={buildFormPath('validityPeriod.enabled', basePath)}
          label={t(
            'chargingPolicy.pricing.custom.validityPeriodEnabled',
            'This tariff has a validity period'
          )}
        />
        {values?.validityPeriod?.enabled && (
          <>
            <p
              style={{
                fontSize: '0.85rem',
                marginLeft: '4.6rem',
                marginTop: '-0.8rem',
              }}>
              {t(
                'chargingPolicy.pricing.custom.validityPeriod.description',
                'Ensure that your tariff components with date restrictions are within this tariff validity period.'
              )}
            </p>
            <>
              <Divider />
              <Form.Group>
                <DateField
                  name={buildFormPath('validityPeriod.start', basePath)}
                  label={t(
                    'chargingPolicy.pricing.restrictions.startDate',
                    'Start date'
                  )}
                />
                <DateField
                  name={buildFormPath('validityPeriod.end', basePath)}
                  label={t(
                    'chargingPolicy.pricing.restrictions.endDate',
                    'End date'
                  )}
                />
              </Form.Group>
            </>
          </>
        )}
        <DropdownField
          selection
          required
          name={buildFormPath('authorizationMethod', basePath)}
          label={t(
            'chargingPolicy.pricing.custom.paymentOption',
            'Payment option'
          )}
          options={getPaymentOptions()}
        />
        <DropdownField
          selection
          required
          multiple
          name={buildFormPath('customerGroups', basePath)}
          label={t(
            'chargingPolicy.pricing.custom.customerGroups',
            'Customer groups'
          )}
          options={getCustomerGroups()}
        />
        <CustomerGroupModal
          // TODO: Load values into modal
          trigger={
            <Button
              basic
              icon="plus"
              content={t(
                'chargingPolicy.pricing.custom.addCustomerGroup',
                'New Customer Group'
              )}
            />
          }
          onSubmit={(newCustomerGroupId) => {
            setFieldValue(buildFormPath('customerGroups', basePath), [
              ...(values?.customerGroups ?? []),
              newCustomerGroupId,
            ]);
          }}
        />

        <Segment>
          <FormController
            openInEditMode={true}
            values={values?.tariff}
            onSubmit={(values) => {
              setFieldValue(buildFormPath('tariff', basePath), {
                ...values,
                __initial: undefined,
              });
            }}
            onRemove={() =>
              setFieldValue(buildFormPath('tariff', basePath), {})
            }
            formComponent={TariffFormFields}
            previewComponent={TariffPreview}></FormController>
        </Segment>
      </Segment>
    </>
  );
};
